import React, { Suspense, lazy, useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { LanguageToggle } from "@/overlay/LanguageToggle";
import { AudioToggle } from "@/overlay/AudioToggle";
import { CustomCursor } from "@/overlay/CustomCursor";
import { ScrollProgress } from "@/overlay/ScrollProgress";
import { ScrollChoreographer } from "@/overlay/ScrollChoreographer";
import { IntroLoader } from "@/overlay/IntroLoader";
import { HeroSection } from "@/sections/HeroSection";
import { AboutSection } from "@/sections/AboutSection";
import { ContactSection } from "@/sections/ContactSection";

const SceneCanvas = lazy(() =>
  import("@/scene/SceneCanvas").then((m) => ({ default: m.SceneCanvas })),
);

function App() {
  const [introDone, setIntroDone] = useState(() => {
    if (typeof window === "undefined") return false;
    // Show intro once per session
    try {
      return sessionStorage.getItem("poseidon_intro_seen") === "1";
    } catch {
      return false;
    }
  });

  const handleIntroDone = () => {
    setIntroDone(true);
    try {
      sessionStorage.setItem("poseidon_intro_seen", "1");
    } catch {}
  };

  return (
    <div
      id="scroll-root"
      className="relative bg-ink-950 text-[var(--paper-50)] min-h-screen"
      data-testid="app-root"
      style={{
        overflow: introDone ? "visible" : "hidden",
      }}
    >
      {/* Fallback background (visible before WebGL loads) */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 0,
          background:
            "radial-gradient(1200px 800px at 50% 28%, rgba(61,198,224,0.10) 0%, rgba(10,22,40,0) 55%), radial-gradient(900px 700px at 22% 78%, rgba(212,175,55,0.06) 0%, rgba(10,22,40,0) 60%), linear-gradient(180deg, #05070B 0%, #0A1628 60%, #05070B 100%)",
        }}
      />

      {/* 3D Scene (fixed behind content) */}
      <Suspense fallback={null}>
        <SceneCanvas />
      </Suspense>

      {/* Overlays */}
      <div className="noise-overlay" />
      <div className="vignette-overlay" />

      {/* Top HUD */}
      <div
        className="fixed top-5 sm:top-6 left-0 right-0 z-50 pointer-events-none"
        data-testid="top-hud"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <BrandMark />
          <div className="pointer-events-auto">
            <LanguageToggle />
          </div>
        </div>
      </div>

      {/* Audio toggle - bottom-left to avoid Emergent badge */}
      <div
        className="fixed bottom-5 left-5 sm:left-6 z-50 pointer-events-auto"
        data-testid="bottom-hud"
      >
        <AudioToggle />
      </div>

      <ScrollProgress />
      <CustomCursor />
      <ScrollChoreographer />

      {/* Intro loader */}
      {!introDone && <IntroLoader onDone={handleIntroDone} />}

      {/* Content */}
      <main className="relative z-10">
        <HeroSection />
        <AboutSection />
        <ContactSection />
      </main>

      <Toaster
        theme="dark"
        position="top-left"
        richColors={false}
        toastOptions={{
          style: {
            background: "rgba(10,22,40,0.86)",
            border: "1px solid rgba(240,196,90,0.22)",
            color: "#F3EFE6",
            backdropFilter: "blur(10px)",
            borderLeft: "2px solid #F0C45A",
          },
          className: "font-mono-brand",
        }}
      />
    </div>
  );
}

function BrandMark() {
  return (
    <div
      className="flex items-center gap-3 pointer-events-auto select-none"
      data-testid="brand-mark"
    >
      <img
        src="/assets/poseidon-logo-v3.png"
        alt="Poseidon Music"
        width={30}
        height={30}
        className="opacity-95"
        style={{ filter: "drop-shadow(0 0 12px rgba(240,196,90,0.35))" }}
      />
      <div className="leading-[1.0]">
        <div className="font-display-en text-[17px] tracking-[-0.01em] text-[var(--paper-50)]">
          Poseidon
        </div>
        <div className="mt-[3px] text-[10px] tracking-[0.3em] uppercase font-mono-brand text-[var(--gold-400)]/90">
          海神 · MUSIC
        </div>
      </div>
    </div>
  );
}

export default App;
